import React, { useEffect, useState } from 'react';
import { FiX, FiFolder, FiTrash2 } from 'react-icons/fi';
import { apiFetch } from '../api';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4000,
};

const modalStyle = {
  backgroundColor: '#ffffff',
  width: '95%',
  maxWidth: '420px',
  maxHeight: '80vh',
  borderRadius: '14px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '14px 16px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const listStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  overflowY: 'auto',
};

const itemRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '12px 16px',
  borderBottom: '1px solid #f1f5f9',
};

const searchButtonStyle = {
  width: '100%',
  padding: '10px',
  borderRadius: '10px',
  border: '1px solid #5eead4',
  background: '#e6fffa',
  color: '#0f766e',
  fontWeight: 700,
  cursor: 'pointer',
};

const conflictButtonStyle = {
  flex: 1,
  padding: '10px',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  cursor: 'pointer',
  fontWeight: 600,
};

// SHOP-225: GET /watched-categories returns [{name, name_es}] (name is the
// English canonical identity used for add/remove/search; name_es is a
// store-independent Spanish label). Also accepts the legacy flat
// list-of-strings shape.
export const normalizeWatchedCategories = (data) =>
  (data || [])
    .map((entry) =>
      typeof entry === 'string'
        ? { name: entry, name_es: null }
        : { name: entry?.name, name_es: entry?.name_es || null }
    )
    .filter((entry) => entry.name);

// SHOP-225: localized label for a watched category name. Prefers the
// backend-provided Spanish label (covers categories missing from the
// store-filtered catalog), then the catalog's name_es, then the English name.
export const getWatchedCategoryLabel = (name, language, spanishLabels = {}, allCategories = []) => {
  if (language !== 'es') return name;
  return (
    spanishLabels[name] ||
    allCategories.find((c) => c.name === name)?.name_es ||
    name
  );
};

// SHOP-45: mirrors WatchedItemsModal.jsx's shell, but simpler content -- just
// the category name and a remove button (no price-trend UI, since watched
// categories have no per-item price data). Remove-only, same as
// WatchedItemsModal: adding a watched category happens via the group-header
// toggle icon (SHOP-43), not from this modal.
//
// SHOP-48: also hosts the "search with watched categories" trigger. Per-row
// availability is computed against `allCategories` (the same store-filtered
// catalog the manual CATEGORIES screen uses), since that catalog only ever
// contains categories with >=1 active item for the currently selected
// stores -- a watched name absent from it has 0 available items.
const WatchedCategoriesModal = ({
  isOpen,
  onClose,
  translations,
  language,
  onRemoveWatchedCategory,
  allCategories = [],
  hasManualSelection = false,
  onSearchWatchedCategories,
}) => {
  const [categories, setCategories] = useState([]);
  const [spanishLabels, setSpanishLabels] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [step, setStep] = useState('list'); // 'list' | 'conflict'
  const [showAllUnavailable, setShowAllUnavailable] = useState(false);
  const [pendingSplit, setPendingSplit] = useState({ valid: [], invalid: [] });

  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    setHasError(false);
    setStep('list');
    setShowAllUnavailable(false);

    apiFetch('/watched-categories')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load watched categories');
        return res.json();
      })
      .then((data) => {
        const entries = normalizeWatchedCategories(data);
        setCategories(entries.map((e) => e.name));
        setSpanishLabels(
          entries.reduce((acc, e) => {
            if (e.name_es) acc[e.name] = e.name_es;
            return acc;
          }, {})
        );
      })
      .catch((err) => {
        console.error('Error loading watched categories:', err);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  const getLabel = (category) =>
    getWatchedCategoryLabel(category, language, spanishLabels, allCategories);

  // SHOP-225: sort by the localized label, matching the Categories screen.
  const sortedCategories = [...categories].sort((a, b) =>
    getLabel(a).localeCompare(getLabel(b), language)
  );

  const getItemCount = (category) =>
    allCategories.find((c) => c.name === category)?.itemCount || 0;

  const handleSearchClick = () => {
    const valid = categories.filter((c) => getItemCount(c) > 0);
    const invalid = categories.filter((c) => getItemCount(c) <= 0);

    if (valid.length === 0) {
      setShowAllUnavailable(true);
      return;
    }

    setShowAllUnavailable(false);

    if (hasManualSelection) {
      setPendingSplit({ valid, invalid });
      setStep('conflict');
      return;
    }

    onSearchWatchedCategories?.(valid, invalid, 'replace', invalid.map(getLabel));
  };

  const handleConflictChoice = (mode) => {
    onSearchWatchedCategories?.(
      pendingSplit.valid,
      pendingSplit.invalid,
      mode,
      pendingSplit.invalid.map(getLabel)
    );
    setStep('list');
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleRemove = (category) => {
    setCategories((prev) => prev.filter((c) => c !== category));
    onRemoveWatchedCategory?.(category);
  };

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3
            style={{
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1.1em',
              fontWeight: 700,
            }}
          >
            <FiFolder />
            {translations?.myWatchedCategories || 'My Watched Categories'}
          </h3>

          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '1.2em',
              cursor: 'pointer',
            }}
            aria-label={translations?.modalClose || 'Close'}
          >
            <FiX size={20} />
          </button>
        </div>

        <ul style={listStyle}>
          {isLoading && (
            <li style={{ padding: '16px', color: '#6b7280' }}>
              {translations?.loadingCategories || 'Loading categories...'}
            </li>
          )}

          {!isLoading && hasError && (
            <li style={{ padding: '16px', color: '#dc2626' }}>
              {translations?.categoryWatchToggleError || "Couldn't update the watched category."}
            </li>
          )}

          {!isLoading && !hasError && categories.length === 0 && (
            <li style={{ padding: '16px', color: '#6b7280' }}>
              {translations?.watchedCategoriesEmpty || 'No watched categories yet.'}
            </li>
          )}

          {!isLoading &&
            !hasError &&
            sortedCategories.map((category) => {
              const itemCount = getItemCount(category);
              const isUnavailable = itemCount <= 0;

              return (
                <li key={category} style={itemRowStyle}>
                  <div style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                    {getLabel(category)}
                    <span
                      style={{
                        marginLeft: '6px',
                        fontSize: '0.85em',
                        fontWeight: 'normal',
                        opacity: 0.6,
                      }}
                    >
                      ({itemCount})
                    </span>
                    {isUnavailable && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: '0.75em',
                          fontWeight: 'normal',
                          color: '#dc2626',
                        }}
                      >
                        {translations?.watchedCategoryUnavailable ||
                          'Not available for the selected stores'}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemove(category)}
                    aria-label={translations?.unwatchCategory || 'Unwatch category'}
                    title={translations?.unwatchCategory || 'Unwatch category'}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: '#dc2626',
                      padding: '6px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FiTrash2 size={18} />
                  </button>
                </li>
              );
            })}
        </ul>

        {!isLoading && !hasError && categories.length > 0 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
            {step === 'list' && (
              <>
                {showAllUnavailable && (
                  <p style={{ margin: '0 0 8px', fontSize: '0.85em', color: '#dc2626' }}>
                    {translations?.watchedCategoriesAllUnavailable ||
                      'None of your watched categories have items available for the selected stores.'}
                  </p>
                )}
                <button type="button" onClick={handleSearchClick} style={searchButtonStyle}>
                  {translations?.searchWatchedCategories || 'Search with watched categories'}
                </button>
              </>
            )}

            {step === 'conflict' && (
              <div>
                <p style={{ margin: '0 0 10px', fontSize: '0.9em', color: '#334155' }}>
                  {translations?.watchedCategoriesConflictPrompt ||
                    'You already have categories selected. Search with only your watched categories, or combine them with your current selection?'}
                </p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <button
                    type="button"
                    onClick={() => handleConflictChoice('replace')}
                    style={conflictButtonStyle}
                  >
                    {translations?.watchedCategoriesConflictWatchedOnly || 'Watched only'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleConflictChoice('union')}
                    style={conflictButtonStyle}
                  >
                    {translations?.watchedCategoriesConflictUnion || 'Combine with current selection'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setStep('list')}
                  style={{ border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer' }}
                >
                  {translations?.cancel || 'Cancel'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchedCategoriesModal;
